import React, { useState } from "react";
import {
  MoreVertical, Trash2, MessageCircle, ArrowRightLeft, RotateCcw,
  CheckCircle2, History,
} from "lucide-react";
import { toast } from "sonner";
import {
  removeSectionUpload, requestSectionReupload, moveSectionVersion,
  errorMessage,
} from "../../lib/api";

/** Compact "Manage" (⋮) menu that consolidates every owner action for a
 * single section. Aligned with the "one button = one outcome" principle —
 * owner sees a single icon; the complexity opens on demand.
 */
export default function SectionManageMenu({ group, section, onChanged, onOpenHistory }) {
  const [open, setOpen] = useState(false);
  const [showMove, setShowMove] = useState(false);
  const [showReupload, setShowReupload] = useState(false);
  const [moveTarget, setMoveTarget] = useState("");
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  const cur = (section.versions || []).find((v) => v.is_current);
  const hasUpload = !!cur;
  const otherSections = (group.sections || []).filter((s) => s.id !== section.id);

  const doAction = async (fn, successMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(successMsg);
      setOpen(false); setShowMove(false); setShowReupload(false);
      onChanged?.();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally { setBusy(false); }
  };

  const doRemove = () => {
    if (!window.confirm("Remove the current upload? The section will go back to 'Awaiting Upload'. All versions stay in history and can be restored.")) return;
    return doAction(
      () => removeSectionUpload(group.id, section.id, {}),
      "Upload removed."
    );
  };
  const doMove = () => moveTarget && doAction(
    () => moveSectionVersion(group.id, section.id, cur.id, moveTarget),
    `Moved to ${otherSections.find((s) => s.id === moveTarget)?.title}.`
  );
  const doReupload = () => doAction(
    () => requestSectionReupload(group.id, section.id, { reason, notify_email: notify }),
    notify ? "Re-upload requested — member notified." : "Re-upload requested."
  );

  return (
    <div className="relative inline-block" data-testid={`section-manage-menu-${section.id}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={group.locked}
        data-testid={`section-manage-toggle-${section.id}`}
        className="p-1.5 rounded-md hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Manage section"
        title={group.locked ? "Group is locked — unlock to make changes" : "Manage this section"}
      >
        <MoreVertical size={16} />
      </button>

      {open && !group.locked && (
        <>
          {/* Click-outside overlay */}
          <div className="fixed inset-0 z-10" onClick={() => { setOpen(false); setShowMove(false); setShowReupload(false); }} />
          <div className="absolute right-0 top-full mt-1 w-64 rounded-lg border border-zinc-200 bg-white shadow-lg z-20 overflow-hidden" data-testid={`section-manage-dropdown-${section.id}`}>
            {/* Primary quick actions */}
            {hasUpload && !showMove && !showReupload && (
              <div className="py-1">
                <button
                  onClick={() => setShowMove(true)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 inline-flex items-center gap-2"
                  data-testid={`section-menu-move-${section.id}`}
                >
                  <ArrowRightLeft size={14} className="text-indigo-600" /> Move to another section
                </button>
                <button
                  onClick={() => setShowReupload(true)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 inline-flex items-center gap-2"
                  data-testid={`section-menu-reupload-${section.id}`}
                >
                  <MessageCircle size={14} className="text-amber-600" /> Request re-upload
                </button>
                <button
                  onClick={doRemove}
                  disabled={busy}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-red-50 text-red-700 inline-flex items-center gap-2"
                  data-testid={`section-menu-remove-${section.id}`}
                >
                  <Trash2 size={14} /> Remove current upload
                </button>
              </div>
            )}

            {/* Always show: history */}
            {(section.versions || []).length > 0 && !showMove && !showReupload && (
              <div className="border-t border-zinc-100 py-1">
                <button
                  onClick={() => { onOpenHistory?.(); setOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-zinc-50 inline-flex items-center gap-2"
                  data-testid={`section-menu-history-${section.id}`}
                >
                  <History size={14} className="text-zinc-500" />
                  Version history · {(section.versions || []).length} version{(section.versions || []).length === 1 ? "" : "s"}
                </button>
              </div>
            )}

            {/* Move-to picker */}
            {showMove && (
              <div className="p-3 border-t border-zinc-100" data-testid={`section-manage-move-picker-${section.id}`}>
                <p className="text-xs font-semibold text-zinc-700 mb-2">Move to</p>
                <select
                  value={moveTarget}
                  onChange={(e) => setMoveTarget(e.target.value)}
                  className="w-full text-sm rounded border border-zinc-200 px-2 py-1.5 mb-2"
                  data-testid={`section-manage-move-target-${section.id}`}
                >
                  <option value="">Pick a section…</option>
                  {otherSections.sort((a, b) => a.order - b.order).map((s) => (
                    <option key={s.id} value={s.id}>{s.title}</option>
                  ))}
                </select>
                <div className="flex gap-2">
                  <button
                    onClick={doMove}
                    disabled={!moveTarget || busy}
                    data-testid={`section-manage-move-confirm-${section.id}`}
                    className="flex-1 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-md py-1.5 disabled:opacity-40"
                  >
                    Move
                  </button>
                  <button
                    onClick={() => setShowMove(false)}
                    className="text-xs text-zinc-600 px-2"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Re-upload form */}
            {showReupload && (
              <div className="p-3 border-t border-zinc-100" data-testid={`section-manage-reupload-form-${section.id}`}>
                <p className="text-xs font-semibold text-zinc-700 mb-2">Ask for a re-upload</p>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  placeholder="Optional reason (shown to the member)"
                  className="w-full text-xs rounded border border-zinc-200 px-2 py-1.5 mb-2"
                />
                <label className="text-[11px] text-zinc-700 inline-flex items-center gap-1.5 mb-2">
                  <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
                  Email the member
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={doReupload}
                    disabled={busy}
                    data-testid={`section-manage-reupload-confirm-${section.id}`}
                    className="flex-1 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded-md py-1.5"
                  >
                    Send request
                  </button>
                  <button
                    onClick={() => setShowReupload(false)}
                    className="text-xs text-zinc-600 px-2"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {!hasUpload && !showMove && !showReupload && (
              <p className="px-3 py-3 text-xs text-zinc-500 italic">No upload yet — no actions available.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
